import {Children, createContext, isValidElement, useContext, type ComponentProps, type ReactNode} from 'react';
import {cn} from '../lib/utils';
import type {AccentColor} from '../lib/color';
import type {Testable} from '../lib/testable';
import {FailIcon, InfoIcon, Success2Icon, WarningIcon} from '../icons';
import type {IconComponent} from '../icons';
import {Typography} from '../Typography';

export type AlertProps = ComponentProps<'div'> & Testable & {
	variant?: AccentColor;
	title?: string;
};

export const Alert = ({
	variant = 'info', title, testId, className, children, ...props
}: AlertProps) => {
	const Icon = variantIcons[variant];
	const parts = Children.toArray(children);
	const actions = parts.filter(isAlertAction);
	const body = parts.filter(part => !isAlertAction(part));

	return (
		<AlertContext.Provider value={true}>
			<div
				data-component='Alert'
				data-testid={testId}
				role={variant === 'critical' || variant === 'medium' ? 'alert' : 'status'}
				className={cn(
					className,
					baseStyles,
					actions.length ? 'grid-cols-[auto_minmax(0,1fr)_auto]' : 'grid-cols-[auto_minmax(0,1fr)]',
					variantStyles[variant]
				)}
				{...props}>
				<Icon color={variant} size='md' className='shrink-0'/>
				{title && <Typography data-component='AlertTitle' variant='body2'>{title}</Typography>}
				{body.length > 0 && (
					<Typography data-component='AlertDescription' component='div' variant='body3' color='textSecondary'>
						{body}
					</Typography>
				)}
				{actions}
			</div>
		</AlertContext.Provider>
	);
};

Alert.displayName = 'Alert';

export type AlertActionProps = ComponentProps<'div'> & Testable;

export const AlertAction = ({
	testId, className, children, ...props
}: AlertActionProps) => {
	useInAlert('AlertAction');

	return (
		<div
			data-component='AlertAction'
			data-testid={testId}
			className={cn(className, 'flex items-center gap-2')}
			{...props}>
			{children}
		</div>
	);
};

AlertAction.displayName = 'AlertAction';

const AlertContext = createContext(false);

function useInAlert(part: string) {
	const inAlert = useContext(AlertContext);
	if (!inAlert) throw new Error(`${part} must be rendered inside Alert.`);
}

const isAlertAction = (part: ReactNode) => isValidElement(part) && part.type === AlertAction;

const baseStyles =
	'grid w-full items-center gap-x-3 gap-y-1 rounded-lg border border-solid px-4 py-3 ' +
	'[&>*]:col-start-2 [&>svg]:col-start-1 [&>svg]:row-start-1 ' +
	'[&>[data-component=AlertAction]]:col-start-3 [&>[data-component=AlertAction]]:row-start-1';

const variantStyles: Record<AccentColor, string> = {
	primary: 'border-brand-main bg-brand-main/10',
	info: 'border-tone-teal bg-tone-teal/10',
	success: 'border-tone-green bg-tone-green/10',
	low: 'border-tone-yellow bg-tone-yellow/10',
	medium: 'border-tone-orange bg-tone-orange/10',
	critical: 'border-tone-red bg-tone-red/10'
};

const variantIcons: Record<AccentColor, IconComponent> = {
	primary: InfoIcon,
	info: InfoIcon,
	success: Success2Icon,
	low: InfoIcon,
	medium: WarningIcon,
	critical: FailIcon
};
